/**
 * Samples scroll container, message bubbles, and typewriter cursor positions while layout debug is enabled.
 */

import { useEffect } from 'react';
import {
  isLayoutDebugEnabled,
  noteLayoutDebugEvent,
  recordLayoutDebugSample,
  registerLayoutDebugConsoleApi,
  serializeRect,
  type LayoutDebugBubbleState,
  type LayoutDebugSample,
} from '../utils/layoutDebug';

export interface UseLayoutDebugRecorderOptions {
  enabled: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
}

function collectBubbleStates(root: HTMLElement | null): LayoutDebugBubbleState[] {
  if (!root) return [];

  const bubbles: LayoutDebugBubbleState[] = [];
  const contentNodes = root.querySelectorAll<HTMLElement>('.chatbot-message-content');

  contentNodes.forEach((contentEl) => {
    const bubbleShell = contentEl.closest<HTMLElement>('.group.relative');
    const messageRow = contentEl.closest<HTMLElement>('.chatbot-message');
    if (!bubbleShell || !messageRow) return;

    const id =
      bubbleShell.dataset.layoutMessageId ??
      messageRow.dataset.layoutMessageId ??
      `bubble-${bubbles.length}`;
    const offset = bubbleShell.dataset.layoutMessageOffset ?? '';
    const cursorEl = contentEl.querySelector<HTMLElement>('span.animate-pulse[aria-hidden="true"]');
    const contentRect = serializeRect(contentEl.getBoundingClientRect());

    bubbles.push({
      id,
      offset,
      rect: serializeRect(bubbleShell.getBoundingClientRect())!,
      contentRect,
      contentHeight: contentEl.scrollHeight,
      hasCursor: cursorEl !== null,
    });
  });

  return bubbles;
}

function findActiveBubbleId(bubbles: LayoutDebugBubbleState[]): string | null {
  const withCursor = [...bubbles].reverse().find((bubble) => bubble.hasCursor);
  if (withCursor) return withCursor.id;

  const lastAgent = [...bubbles].reverse().find((bubble) => bubble.contentHeight > 0);
  return lastAgent?.id ?? null;
}

function collectSample(
  container: HTMLDivElement | null,
  content: HTMLDivElement | null
): LayoutDebugSample | null {
  if (!container) return null;

  const t = performance.now();
  const scrollTop = container.scrollTop;
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;
  const bubbles = collectBubbleStates(content);
  const activeBubbleId = findActiveBubbleId(bubbles);
  const activeContent = activeBubbleId
    ? content?.querySelector<HTMLElement>(`[data-layout-message-id="${activeBubbleId}"] .chatbot-message-content`) ??
      content?.querySelector<HTMLElement>('.chatbot-message-content:last-of-type')
    : content?.querySelector<HTMLElement>('.chatbot-message-content:last-of-type');
  const cursorEl = activeContent?.querySelector<HTMLElement>('span.animate-pulse[aria-hidden="true"]');

  return {
    t,
    scroll: {
      scrollTop: Math.round(scrollTop * 10) / 10,
      scrollHeight: Math.round(scrollHeight * 10) / 10,
      clientHeight: Math.round(clientHeight * 10) / 10,
      distanceFromBottom: Math.round((scrollHeight - scrollTop - clientHeight) * 10) / 10,
    },
    content: serializeRect(content?.getBoundingClientRect() ?? null),
    bubbles,
    cursor: serializeRect(cursorEl?.getBoundingClientRect() ?? null),
    activeBubbleId,
    delta: null,
  };
}

export function useLayoutDebugRecorder({
  enabled,
  containerRef,
  contentRef,
}: UseLayoutDebugRecorderOptions): void {
  useEffect(() => {
    if (!enabled) return;

    registerLayoutDebugConsoleApi();
    noteLayoutDebugEvent('recorder-start');

    let rafId = 0;

    const tick = (): void => {
      if (!isLayoutDebugEnabled()) return;

      const sample = collectSample(containerRef.current, contentRef.current);
      if (sample) {
        recordLayoutDebugSample(sample);
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    const onKeyDown = (event: KeyboardEvent): void => {
      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 'l') return;
      event.preventDefault();
      window.__layoutDebug?.download();
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('keydown', onKeyDown);
      noteLayoutDebugEvent('recorder-stop');
    };
  }, [enabled, containerRef, contentRef]);
}
